/**
 * Contact — public contact form. Wires POST /api/contact.
 *
 * Route: `/contact` (public). Accepts `?source=` query param used by
 * the Enterprise CTA on `/partner/tiers` — auto-populates the source
 * field so admin can triage enterprise leads separately.
 *
 * Backend contract: `POST /api/contact` (persist-first, email-second).
 * The endpoint always returns 200 when validation passes; the
 * `email_dispatched` flag indicates whether the admin heads-up email
 * actually got through. We surface a positive toast on 200 and a
 * distinct warning if email delivery failed (so the user knows we
 * still have their message).
 *
 * Fields (mirrors the ContactRequest Pydantic model):
 *   - name (required, non-empty)
 *   - email (required, EmailStr — /^[^\s@]+@[^\s@]+\.[^\s@]+$/)
 *   - business_name (optional)
 *   - message (required, >= 10 chars)
 *   - source (optional; auto-filled from query string if present)
 */
import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PublicPageShell from "@/components/PublicPageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import FieldError from "@/components/a11y/FieldError";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import { Loader2, Mail, ArrowLeft } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [params] = useSearchParams();
  const initialSource = params.get("source") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    message: "",
    source: initialSource,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Keep source in sync with URL changes (e.g. same-page tab switches).
  useEffect(() => {
    setForm((f) => ({ ...f, source: initialSource }));
  }, [initialSource]);

  const nameOk = form.name.trim().length > 0;
  const emailOk = EMAIL_RE.test(form.email.trim());
  const messageOk = form.message.trim().length >= 10;
  const canSubmit = nameOk && emailOk && messageOk && !submitting;

  // Phase L.2 P1 — a11y announcement wiring (audit finding C-1).
  // Errors are only "showing" once the user has typed something in
  // the field; before then the input is untouched and the error
  // would be premature.
  const showEmailError = form.email && !emailOk;
  const showMessageError = form.message && !messageOk;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Only send fields the endpoint expects — omit empty
      // business_name / source so `extra="forbid"` isn't a factor
      // (the endpoint accepts nulls but omitting is cleaner).
      const body = {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      };
      if (form.business_name.trim()) body.business_name = form.business_name.trim();
      if (form.source.trim()) body.source = form.source.trim();

      const { data } = await api.post("/contact", body);
      setSubmitted(true);
      if (data && data.email_dispatched) {
        toast.success("Thanks! We've received your message and will be in touch shortly.");
      } else {
        // Persist happened but email didn't — the lead is safe, but
        // let the user know so they know it went through.
        toast.success("Thanks! Your message is saved. We'll be in touch soon.");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) ||
                  "Could not send your message. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <PublicPageShell mainClass="px-6 py-16 md:py-24">
        <section
          className="pp-wrap max-w-2xl mx-auto text-center"
          data-testid="contact-page-success"
          role="status"
          aria-live="polite"
        >
          <h1 className="pp-h1 mt-6">Thanks!</h1>
          <p className="pp-sub mt-5 mx-auto max-w-xl" data-testid="contact-success-body">
            We&apos;ve received your message
            {form.source ? ` (${form.source})` : ""}
            {" "}and someone from our team will be in touch shortly.
          </p>
          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-pp-slate hover:underline"
              data-testid="contact-back-home"
            >
              <ArrowLeft size={14} />
              Back to home
            </Link>
          </div>
        </section>
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell mainClass="px-6 py-16 md:py-24">
      <section
        className="pp-wrap max-w-2xl mx-auto"
        data-testid="contact-page"
      >
        <div className="text-center">
          <span className="pp-eyebrow" data-testid="contact-eyebrow">
            <span className="dot" />
            Contact
          </span>
          <h1 className="pp-h1 mt-6" data-testid="contact-headline">
            Get in touch.
          </h1>
          <p className="pp-sub mt-5 mx-auto max-w-xl" data-testid="contact-subtitle">
            {initialSource === "enterprise"
              ? "Big fleet, commercial contract, or something outside our tiers? Tell us about your business and we'll shape the deal around it."
              : "Questions, feedback, or partnership ideas — drop a note and we'll get right back."}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 space-y-5"
          data-testid="contact-form"
          noValidate
        >
          <div>
            <Label htmlFor="contact-name">Your name</Label>
            <Input
              id="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={200}
              data-testid="contact-input-name"
              placeholder="First & last"
            />
          </div>

          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              maxLength={200}
              data-testid="contact-input-email"
              placeholder="you@example.com"
              aria-invalid={showEmailError || undefined}
              aria-describedby={showEmailError ? "contact-email-error" : undefined}
            />
            <FieldError
              id="contact-email-error"
              message={showEmailError ? "Please enter a valid email address." : null}
              testId="contact-email-error"
            />
          </div>

          <div>
            <Label htmlFor="contact-business">
              Business name <span className="text-xs text-gray-500 font-normal">(optional)</span>
            </Label>
            <Input
              id="contact-business"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              maxLength={200}
              data-testid="contact-input-business"
              placeholder="Your business, if applicable"
            />
          </div>

          <div>
            <Label htmlFor="contact-message">Message</Label>
            <Textarea
              id="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              maxLength={5000}
              rows={6}
              data-testid="contact-input-message"
              placeholder="What's on your mind? (10 character minimum)"
              aria-invalid={showMessageError || undefined}
              aria-describedby={showMessageError ? "contact-message-error" : undefined}
            />
            <FieldError
              id="contact-message-error"
              message={showMessageError ? "Please write at least 10 characters." : null}
              testId="contact-message-error"
            />
          </div>

          {/* Hidden source field — populated by ?source= query param. */}
          <input
            type="hidden"
            value={form.source}
            data-testid="contact-input-source"
            readOnly
          />

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full"
            data-testid="contact-submit"
            aria-busy={submitting || undefined}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Sending…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Mail size={14} aria-hidden="true" />
                Send message
              </span>
            )}
          </Button>

          {/* Phase L.2 P2 — audit C-4. Polite live region so screen
              readers announce the submit-in-progress state. Region
              stays mounted (empty string when idle) so the empty →
              "Sending your message…" transition fires correctly. */}
          <PoliteStatus
            visuallyHidden
            message={submitting ? "Sending your message…" : ""}
            testId="contact-submit-status"
          />

          <p className="text-xs text-center text-gray-500" data-testid="contact-privacy-note">
            We&apos;ll only use your email to reply. No newsletter, no spam.
          </p>
        </form>
      </section>
    </PublicPageShell>
  );
}
